import { Camera, FileText, MessageCircle, Stethoscope, type LucideIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { BRAND } from '@medicail/shared';
import { BrandMark } from '@/components/ui';

interface Suggestion {
  text: string;
  icon: LucideIcon;
  hint?: string;
}

const SUGGESTIONS: Suggestion[] = [
  { text: 'My elbow hurts when I grip things', icon: Stethoscope },
  { text: "I've had a headache for a week", icon: MessageCircle },
  { text: 'Help me understand my blood test results', icon: FileText, hint: 'You can attach a PDF or photo' },
  { text: 'Is this rash something to worry about?', icon: Camera, hint: 'A photo helps' },
];

function greeting(name?: string): string {
  const h = new Date().getHours();
  const part = h < 5 ? 'Hello' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  const first = name?.trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

export function EmptyState({ name, onPick, disabled }: { name?: string; onPick: (text: string) => void; disabled?: boolean }) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 pb-6 pt-2 text-center sm:px-6 sm:pt-14">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
        <BrandMark className="size-20 sm:size-28" />
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08, duration: 0.35 }}
        className="mt-4 font-heading text-2xl font-semibold text-ink sm:mt-6 sm:text-4xl"
      >
        {greeting(name)}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.14, duration: 0.35 }}
        className="mt-2 max-w-xl text-sm leading-relaxed text-ink-muted sm:mt-3 sm:text-[0.98rem]"
      >
        {BRAND.description}
      </motion.p>
      <ul className="mt-6 grid w-full sm:mt-8 grid-cols-1 gap-3 text-left sm:grid-cols-2" aria-label="Suggested questions">
        {SUGGESTIONS.map((s, i) => (
          <motion.li key={s.text} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.05, duration: 0.3 }}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onPick(s.text)}
              className="group flex h-full w-full items-start gap-3 rounded-2xl border border-line bg-surface p-3.5 sm:p-4 text-left shadow-soft transition hover:-translate-y-0.5 hover:border-royal-300 hover:shadow-lift disabled:opacity-60"
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-royal-50 text-royal-600 transition-colors group-hover:bg-royal-100 dark:bg-royal-500/15 dark:text-royal-200">
                <s.icon className="size-5" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-[0.95rem] font-medium leading-snug text-ink">{s.text}</span>
                {s.hint && <span className="mt-1 block text-xs text-leaf-600 dark:text-green-300">{s.hint}</span>}
              </span>
            </button>
          </motion.li>
        ))}
      </ul>
    </div>
  );
}
